import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import smoothscroll from 'smoothscroll-polyfill';
import type { Product } from 'src/models/product/schema';
import { mixpanel } from 'src/utils/mixpanel';

const SEARCH_PAGE_INDEX_MIXPANEL_EVENT_NAME = 'SearchPageIndexClicked';

export const usePaginator = () => {
  const router = useRouter();
  const [pageIndex, setPageIndex] = useState<number>(() => {
    return parseInt(router.query.page as string, 10) || 1;
  });
  const [isLoading, setIsLoading] = useState(false);
  const isRouteReady = router.isReady && !isLoading;

  useEffect(() => {
    const currentPage = parseInt(router.query.page as string, 10) || 1;
    if (!isLoading && pageIndex !== currentPage) {
      setIsLoading(true);
      const newQuery: Partial<{ page: number }> = { ...router.query, page: pageIndex };
      if (pageIndex === 1) {
        delete newQuery.page;
      }
      router
        .push({
          pathname: router.pathname,
          query: newQuery
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex]);

  const pageIndexClickHandler = (page: number, shouldScroll: boolean = true, fromFeaturedDetail: boolean = false) => {
    const elm = document.getElementById('goodsCard');
    if (elm && shouldScroll) {
      const contentPosition = elm.getBoundingClientRect();
      const elmTop = contentPosition.top + window.pageYOffset;
      smoothscroll.polyfill();
      window.scroll({ top: fromFeaturedDetail ? 0 : elmTop - 70, behavior: 'smooth' });
    }
    setPageIndex(page);
  };
  return {
    pageIndex,
    setPageIndex,
    pageIndexClickHandler,
    routeLoading: !isRouteReady
  };
};

type Props = {
  products: Product[] | undefined;
};

// TODO useSearchProductsでも`totalPageCount` が返却されるようになれば、usePaginatorと統一して削除
export const usePaginatorForSearch = ({ products }: Props) => {
  const [pageIndex, setPageIndex] = useState<number>(1);
  const numPages = useMemo(() => {
    if (!products) return 1;
    return Math.max(1, Math.ceil(products.length / 20));
  }, [products]);

  const pageIndexClickHandler = (page: number, hasKeyword: boolean) => {
    hasKeyword && mixpanel.track(SEARCH_PAGE_INDEX_MIXPANEL_EVENT_NAME, { type: page });
    const elm = document.getElementById('goodsCard');
    if (elm) {
      const contentPosition = elm.getBoundingClientRect();
      const elmTop = contentPosition.top + window.pageYOffset;
      smoothscroll.polyfill();
      window.scroll({ top: elmTop - 70, behavior: 'smooth' });
    }
    setPageIndex(page);
  };
  return {
    pageIndex,
    numPages,
    pageIndexClickHandler
  };
};
